import React from 'react';

import { Close as CloseIcon } from '@mui/icons-material';
import { Dialog, DialogContent, DialogActions, Button, IconButton, Box, Slide } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';

/* eslint-disable react/require-default-props */
interface PromoBannerVideoSource {
  src: string;
  type: string;
}

interface PromoBannerProps {
  imageUrl?: string;
  /** Muted looping video; list lighter formats first so the browser picks them when supported. */
  videoSources?: PromoBannerVideoSource[];
  /** Shows the native player controls so the video can be paused, unmuted or played fullscreen. */
  videoControls?: boolean;
  /** Image shown while the video loads. */
  videoPoster?: string;
  text?: string;
  header?: string;
  footer?: string;
  callToActionUrl?: string;
  callToActionText?: string;
  /** In-app action for the button; takes precedence over opening callToActionUrl. */
  onCallToActionClick?: () => void;
  onDismiss: () => void;
  isVisible: boolean;
}

// Slide transition for modal entrance
const SlideTransition = React.forwardRef<HTMLDivElement, TransitionProps & { children: React.ReactElement }>(
  function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
  }
);

const PromoBanner: React.FC<PromoBannerProps> = ({
  imageUrl,
  videoSources,
  videoControls = false,
  videoPoster,
  text,
  header,
  footer,
  callToActionUrl,
  callToActionText = 'Registrarme Ahora',
  onCallToActionClick,
  onDismiss,
  isVisible,
}) => {
  const handleDismiss = () => {
    onDismiss();
  };

  const hasCallToAction = Boolean(callToActionUrl || onCallToActionClick);

  const handleCallToActionClick = (): void => {
    if (onCallToActionClick) {
      onCallToActionClick();
    } else if (callToActionUrl) {
      window.open(callToActionUrl, '_blank', 'noopener,noreferrer');
    }
    onDismiss();
  };

  return (
    <Dialog
      open={isVisible}
      onClose={handleDismiss}
      TransitionComponent={SlideTransition}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
          overflow: 'hidden',
          position: 'relative',
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
        },
      }}
    >
      {/* Close button */}
      <IconButton
        onClick={handleDismiss}
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          zIndex: 1,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 1)',
          },
          boxShadow: 2,
        }}
        size="small"
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      {/* Banner content */}
      <DialogContent
        sx={{
          p: 3,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          backgroundColor: '#f8f9fa',
          gap: 3,
        }}
      >
        {/* Banner header */}
        {header && (
          <Box
            sx={{
              textAlign: 'center',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              color: '#1976d2',
              mb: 1,
            }}
          >
            {header}
          </Box>
        )}

        {/* Banner image */}
        {imageUrl && (
          <Box
            component="img"
            src={imageUrl}
            alt="Promotional banner"
            sx={{
              width: '100%',
              height: 'auto',
              objectFit: 'contain',
              display: 'block',
            }}
          />
        )}

        {videoSources && videoSources.length > 0 && (
          <Box
            component="video"
            autoPlay
            muted
            loop
            playsInline
            controls={videoControls}
            controlsList={videoControls ? 'nodownload noplaybackrate' : undefined}
            disablePictureInPicture={videoControls}
            poster={videoPoster}
            sx={{ width: '100%', height: 'auto', display: 'block', borderRadius: 2 }}
          >
            {videoSources.map((source) => (
              <source key={source.src} src={source.src} type={source.type} />
            ))}
          </Box>
        )}

        {/* Banner text */}
        {text && (
          <Box
            sx={{
              textAlign: 'center',
              fontSize: '1.1rem',
              lineHeight: 1.6,
              color: '#333',
              fontWeight: 400,
              maxWidth: '600px',
              px: 2,
            }}
          >
            {text}
          </Box>
        )}
      </DialogContent>

      {/* Action buttons and footer */}
      {(hasCallToAction || footer) && (
        <DialogActions
          sx={{
            p: 3,
            justifyContent: 'center',
            backgroundColor: '#f8f9fa',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {/* Call-to-action button */}
          {hasCallToAction && (
            <Button
              variant="contained"
              onClick={handleCallToActionClick}
              sx={{
                textTransform: 'none',
                fontWeight: 'bold',
                px: 6,
                py: 1.5,
                backgroundColor: '#1976d2',
                '&:hover': {
                  backgroundColor: '#1565c0',
                },
              }}
            >
              {callToActionText}
            </Button>
          )}

          {/* Footer */}
          {footer && (
            <Box
              sx={{
                textAlign: 'center',
                fontSize: '0.875rem',
                color: '#666',
                fontStyle: 'italic',
                mt: hasCallToAction ? 1 : 0,
              }}
            >
              {footer}
            </Box>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default PromoBanner;
